/**
 * The home screen: what needs a person today.
 *
 * Every number here had to pass one test — would a head of Riesgo Corporativo change what they do
 * this morning because of it? That rules out most dashboards: totals that only go up, charts of
 * throughput, a gauge of how busy the machine is. What survives is the work that is waiting on a
 * human, the legajos nobody is looking after, and how often the analysts end up disagreeing with
 * the draft, which is the honest measure of whether the tool is any good.
 */

import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, friendlyError } from "../api/client";
import {
  formatDuration,
  gradeBreakdown,
  metricCards,
  queueRows,
  riskBreakdown,
  timeAgo,
} from "../lib/model";
import type { Tone } from "../lib/model";
import { Button, Chip, Dot, EmptyState, ErrorBanner, PageHeader, Spinner } from "../components/ui";

const TONE_TEXT: Record<Tone, string> = {
  good: "text-good",
  warn: "text-warn",
  bad: "text-bad",
  neutral: "text-ink",
};
const TONE_BAR: Record<Tone, string> = {
  good: "bg-good",
  warn: "bg-warn",
  bad: "bg-bad",
  neutral: "bg-neutral",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const metrics = useQuery({ queryKey: ["metrics"], queryFn: api.metrics, refetchInterval: 15000 });
  // El tablero sólo necesita la primera página: lo que muestra son los que requieren atención.
  const cases = useQuery({
    queryKey: ["cases", "dashboard"],
    queryFn: () => api.listCases({ size: 50 }),
  });

  const attention = useMemo(() => {
    const rows = queueRows(cases.data?.items ?? []);
    // The same order the queue uses: interrupted first, then unfavourable, then the rest.
    return rows.filter((r) => r.needsAttention).slice(0, 6);
  }, [cases.data]);

  if (metrics.isError) return <ErrorBanner {...friendlyError(metrics.error)} />;
  if (!metrics.data) return <Spinner label="Cargando el tablero…" />;
  const m = metrics.data;

  const cards = metricCards(m);
  const risks = riskBreakdown(m);
  const grades = gradeBreakdown(m);
  const graded = grades.reduce((sum, g) => sum + g.count, 0);

  return (
    <>
      <PageHeader
        title="Inicio"
        subtitle={`${m.legajos} legajo(s) activos · ${m.analizados} analizados`}
        actions={
          <Button variant="primary" onClick={() => navigate("/legajos")}>
            Ver la cartera
          </Button>
        }
      />
      <div className="my-5 border-b border-line" />

      {m.legajos === 0 ?
        <EmptyState
          title="Todavía no hay legajos"
          text="Creá el primero desde «Nuevo legajo» y subí los estados contables, el informe de bureau y el memo."
        />
      : <>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line lg:grid-cols-3 xl:grid-cols-6">
            {cards.map((card) => {
              const body = (
                <>
                  <div className={`text-3xl font-semibold ${TONE_TEXT[card.tone ?? "neutral"]}`}>
                    {card.value}
                  </div>
                  <div className="mt-1 text-sm font-medium">{card.label}</div>
                  {card.hint ?
                    <div className="mt-0.5 text-xs leading-snug text-muted">{card.hint}</div>
                  : null}
                </>
              );
              return card.filter ?
                  <Link
                    key={card.label}
                    to={`/legajos?vista=${encodeURIComponent(card.filter)}`}
                    className="bg-white p-4 transition-colors hover:bg-canvas"
                  >
                    {body}
                  </Link>
                : <div key={card.label} className="bg-white p-4">
                    {body}
                  </div>;
            })}
          </div>

          <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <section>
              <h2 className="text-base font-semibold">Requieren atención</h2>
              <p className="mt-0.5 text-sm text-muted">
                Interrumpidos primero, después los desfavorables y los que declaran información
                faltante.
              </p>
              {attention.length === 0 ?
                <p className="mt-4 rounded-lg border border-line bg-canvas px-4 py-6 text-center text-sm text-muted">
                  Nada pendiente. La cartera está al día.
                </p>
              : <ul className="mt-3 divide-y divide-line rounded-lg border border-line">
                  {attention.map((row) => (
                    <li key={row.id}>
                      <Link
                        to={`/legajos/${row.id}`}
                        className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-canvas"
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{row.legalName}</span>
                          <span className="mt-0.5 flex items-center gap-2 text-xs text-muted">
                            <Dot tone={row.state.tone} />
                            {row.state.text}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          {row.redFlags ?
                            <Chip text={`${row.redFlags} alerta(s)`} tone="bad" />
                          : null}
                          {row.risk ?
                            <Chip text={row.risk.text} tone={row.risk.tone} />
                          : null}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              }
            </section>

            <section>
              {graded ?
                <>
                  <h2 className="text-base font-semibold">Calificación de la cartera</h2>
                  <p className="mt-0.5 text-sm text-muted">
                    Scorecard determinístico sobre {graded} legajo(s)
                    {m.calificaciones_parciales ?
                      ` · ${m.calificaciones_parciales} con información parcial`
                    : ""}
                    .
                  </p>
                  <ul className="mt-3 flex gap-px overflow-hidden rounded-lg border border-line bg-line">
                    {grades.map((grade) => (
                      <li key={grade.key} className="flex-1 bg-white px-2 py-3 text-center">
                        <div
                          className={`text-xl font-bold ${grade.count ? TONE_TEXT[grade.tone] : "text-line"}`}
                        >
                          {grade.key}
                        </div>
                        <div
                          className={`mt-0.5 text-sm tabular-nums ${grade.count ? "" : "text-muted"}`}
                        >
                          {grade.count}
                        </div>
                        {grade.partial ?
                          <div
                            className="text-[0.7rem] leading-tight text-warn"
                            title="Calculadas sobre un legajo incompleto"
                          >
                            {grade.partial} parcial{grade.partial === 1 ? "" : "es"}
                          </div>
                        : null}
                      </li>
                    ))}
                  </ul>
                </>
              : null}

              <h2 className={`text-base font-semibold ${graded ? "mt-8" : ""}`}>
                Riesgo en la cartera
              </h2>
              <p className="mt-0.5 text-sm text-muted">
                Sobre los {m.analizados} legajos analizados.
              </p>
              {risks.length === 0 ?
                <p className="mt-4 text-sm text-muted">Todavía no hay análisis terminados.</p>
              : <ul className="mt-3 space-y-3">
                  {risks.map((risk) => (
                    <li key={risk.key}>
                      <div className="flex items-baseline justify-between text-sm">
                        <span>{risk.label}</span>
                        <span className="tabular-nums text-muted">
                          {risk.count} · {Math.round(risk.share * 100)}%
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-line">
                        <div
                          className={`h-full rounded-full ${TONE_BAR[risk.tone]}`}
                          style={{ width: `${Math.max(risk.share * 100, 2)}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              }

              <h2 className="mt-8 text-base font-semibold">El sistema y los analistas</h2>
              <dl className="mt-3 space-y-2 text-sm">
                <Row
                  label="Coincidencia con la sugerencia"
                  value={
                    m.coincidencia_con_el_sistema === null ?
                      "sin decisiones todavía"
                    : `${Math.round(m.coincidencia_con_el_sistema * 100)}% de ${m.decisiones_tomadas}`
                  }
                  hint="Cuántas veces el analista terminó donde apuntaba el borrador"
                />
                <Row
                  label="Análisis en los últimos 7 días"
                  value={String(m.corridas_ultimos_7_dias)}
                />
                <Row label="Duración promedio" value={formatDuration(m.duracion_promedio_seg)} />
                <Row
                  label="Costo de los últimos 30 días"
                  value={`USD ${m.costo_ultimos_30_dias.toFixed(2)}`}
                />
                <Row label="Documentos en el sistema" value={String(m.documentos)} />
              </dl>
              <p className="mt-3 text-xs text-muted">
                Actualizado {timeAgo(new Date().toISOString())}.
              </p>
            </section>
          </div>
        </>
      }
    </>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line pb-2">
      <dt>
        {label}
        {hint ?
          <span className="block text-xs text-muted">{hint}</span>
        : null}
      </dt>
      <dd className="shrink-0 font-medium tabular-nums">{value}</dd>
    </div>
  );
}
